// Card 41, sonar designations (R2626, ui-map-interface.md §17.3.41,
// v2-the-deep.md §13).
//
// Every contact carries its designation beside the dart: S1, S2, ... in the
// order the client first drew it, kept while the picture holds the contact
// (`contact-book.ts`). The class follows the number while a scan reveal
// holds the contact, and a "?" otherwise, because the class is reveal-only
// (numbers.md §7). The historical reading counts sails: "The third sail".
// A squad then names one hull with one word, "S3", and both readers mean
// the same hull.
//
// **WITH THE SURVEY.** The sonar plot is a part of discovery (v2 §13). The
// piece draws while the wire carries a survey, which it does only while
// the flag `discovery` is latched, and it reads no flag itself (v2 §14.1
// rule 5). A world with the flag off, the arcade and the replay viewer
// draw the chart of today. The piece is in no group of the VIEW panel
// (R2702).
//
// **COST.** A live piece: one word a contact, re-written each frame, because
// the dart moves. The book walks the hulls once a frame.

import { chart as S } from '../../../gen/strings';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { contactBook } from './contact-book';
import { CHART_INK, faceOf, inkOf } from './shared';

/** The tag of one contact in a reading. `cls` is '' while no reveal holds
 *  it. The class is a proper name, so both readings write it as the
 *  catalog does, in Title Case ("S4 · Vedette"): no piece writes a name in
 *  capitals (§17.4, R1796, R3248). */
export function designationText(n: number, cls: string, historical: boolean): string {
  if (!historical) return cls === '' ? S.designation_unknown({ n }) : S.designation({ n, cls });
  const nth = S.sail_ordinals[n - 1];
  if (nth === undefined) return S.designation_historical_n({ n });
  return cls === '' ? S.designation_historical({ nth }) : S.designation_historical_cls({ nth, cls });
}

export class DesignationsPiece implements Piece {
  readonly id = 'designations' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  /** A designation belongs to the picture of today: the film hides it. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello'] as const;

  visible(ctx: ChartContext): boolean { return ctx.state.survey.on; }

  /** A new world starts the count again. */
  rebuild(_ctx: ChartContext): void { contactBook.reset(); }

  update(ctx: ChartContext, frame: FrameInfo): void {
    const layer = ctx.scene.fleets;
    if (!layer) return;
    contactBook.observe(layer.chartFleets(), ctx.state.faction, frame.timeS);
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const layer = ctx.scene.fleets;
    if (!layer) return;
    const t = ctx.cfg.pieces.designations;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const ink = inkOf(r.ink, CHART_INK.pale);
    const face = faceOf(ctx.reading);
    for (const f of layer.chartFleets()) {
      const n = contactBook.numberOf(f.id);
      if (n === 0) continue;
      const cls = f.classified ? '' : (ctx.state.ship(f.cls)?.name ?? '');
      ctx.glyphs.word(sinks.quads, designationText(n, cls, hist), f.x + t.fig.tag_dx, f.y + t.fig.tag_dy,
        face, t.text.main, ink, r.alpha * f.alpha);
    }
  }
}

export const designations = new DesignationsPiece();
